// Object-URL cache for photo blobs read out of IndexedDB. Screens render synchronous
// HTML strings, so image tags start with a data-photo-id placeholder and this module
// hydrates their `src` once the (cached, or freshly-read) blob URL resolves.

import { getPhotoBlob } from './db';
import { blobToObjectURL } from './image';

const thumbCache = new Map<string, Promise<string>>();
const originalCache = new Map<string, Promise<string>>();

function resolve(cache: Map<string, Promise<string>>, id: string, variant: 'original' | 'thumbnail') {
  let p = cache.get(id);
  if (!p) {
    p = getPhotoBlob(id, variant).then((b) => (b ? blobToObjectURL(b) : ''));
    cache.set(id, p);
  }
  return p;
}

export function thumbURL(id: string): Promise<string> {
  return resolve(thumbCache, id, 'thumbnail');
}
export function originalURL(id: string): Promise<string> {
  return resolve(originalCache, id, 'original');
}

export function invalidatePhoto(id: string): void {
  for (const cache of [thumbCache, originalCache]) {
    const p = cache.get(id);
    if (p) {
      p.then((url) => url && URL.revokeObjectURL(url)).catch(() => {});
      cache.delete(id);
    }
  }
}

/** Find every `img[data-photo-id]` / `img[data-photo-id-original]` under `root` and
 * fill in its `src` once the blob URL resolves. Call after inserting rendered HTML
 * that contains those placeholders. */
export function hydratePhotoImages(root: ParentNode = document): void {
  root.querySelectorAll<HTMLImageElement>('img[data-photo-id]').forEach((img) => {
    const id = img.dataset.photoId!;
    thumbURL(id).then((url) => {
      if (url) img.src = url;
    });
  });
  root.querySelectorAll<HTMLImageElement>('img[data-photo-id-original]').forEach((img) => {
    const id = img.dataset.photoIdOriginal!;
    originalURL(id).then((url) => {
      if (url) img.src = url;
    });
  });
}
